/**
 * @fileoverview Built-in functions for the Joyfill formulas engine
 * 
 * Provides spreadsheet-style functions that can be used in formulas.
 * All functions are pure (no side effects) and handle edge cases gracefully.
 * Non-numeric values are typically treated as 0 to prevent formula errors.
 */

/**
 * Safely converts a value to a number, returning 0 for invalid values
 * @param {any} value - Value to convert
 * @returns {number} - Valid number or 0
 */
function toNumber(value) {
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * Safely converts a value to a string
 * @param {any} value - Value to convert
 * @returns {string} - String representation
 */
function toString(value) {
  if (value === null || value === undefined) return '';
  return String(value);
}

/**
 * Normalizes arguments to handle both array and spread syntax
 * Example: sum([1,2,3]) and sum(1,2,3) both work
 * @param {any[]} args - Arguments array
 * @returns {any[]} - Flattened array
 */
function normalizeArgs(args) {
  return args.length === 1 && Array.isArray(args[0]) ? args[0] : args;
}

// =============================================================================
// Mathematical Functions
// =============================================================================

/**
 * Calculates the sum of numbers
 * @param {...number|number[]} args - Numbers to sum or array of numbers
 * @returns {number} - Sum of all valid numbers
 * @example sum(1, 2, 3) → 6, sum([1, 2, 3]) → 6
 */
function sum(...args) {
  const values = normalizeArgs(args);
  return values.reduce((acc, val) => acc + toNumber(val), 0);
}

/**
 * Calculates the arithmetic mean of numbers
 * @param {...number|number[]} args - Numbers to average or array of numbers
 * @returns {number} - Average of all valid numbers, or 0 if no valid numbers
 * @example average(1, 2, 3) → 2, average([10, 20]) → 15
 */
function average(...args) {
  const values = normalizeArgs(args);
  const numbers = values.map(toNumber).filter(n => n !== 0 || values.includes(0));
  return numbers.length > 0 ? sum(...numbers) / numbers.length : 0;
}

/**
 * Finds the minimum value
 * @param {...number|number[]} args - Numbers to compare or array of numbers
 * @returns {number} - Minimum value, or 0 if no valid numbers
 * @example min(5, 2, 8) → 2, min([10, 3, 7]) → 3
 */
function min(...args) {
  const values = normalizeArgs(args).map(toNumber);
  return values.length > 0 ? Math.min(...values) : 0;
}

/**
 * Finds the maximum value
 * @param {...number|number[]} args - Numbers to compare or array of numbers
 * @returns {number} - Maximum value, or 0 if no valid numbers
 * @example max(5, 2, 8) → 8, max([10, 3, 7]) → 10
 */
function max(...args) {
  const values = normalizeArgs(args).map(toNumber);
  return values.length > 0 ? Math.max(...values) : 0;
}

/**
 * Rounds a number to specified decimal places
 * @param {number} num - Number to round
 * @param {number} [decimals=0] - Number of decimal places
 * @returns {number} - Rounded number
 * @example round(3.14159, 2) → 3.14, round(42.7) → 43
 */
function round(num, decimals = 0) {
  const n = toNumber(num);
  const d = Math.max(0, Math.floor(toNumber(decimals)));
  const factor = Math.pow(10, d);
  return Math.round(n * factor) / factor;
}

/**
 * Returns the absolute value of a number
 * @param {number} num - Number to get absolute value of
 * @returns {number} - Absolute value
 * @example abs(-5) → 5, abs(3) → 3
 */
function abs(num) {
  return Math.abs(toNumber(num));
}

/**
 * Returns the base raised to the power of exponent
 * @param {number} base - Base number
 * @param {number} exponent - Exponent
 * @returns {number} - Result of base^exponent
 * @example pow(2, 3) → 8, pow(5, 2) → 25
 */
function pow(base, exponent) {
  return Math.pow(toNumber(base), toNumber(exponent));
}

/**
 * Returns the ceiling of a number (rounds up to nearest integer)
 * @param {number} num - Number to ceil
 * @returns {number} - Ceiling value
 * @example ceil(4.2) → 5, ceil(4) → 4
 */
function ceil(num) {
  return Math.ceil(toNumber(num));
}

/**
 * Returns the floor of a number (rounds down to nearest integer)
 * @param {number} num - Number to floor
 * @returns {number} - Floor value
 * @example floor(4.8) → 4, floor(-4.2) → -5
 */
function floor(num) {
  return Math.floor(toNumber(num));
}

/**
 * Returns the remainder after division
 * @param {number} dividend - Number to divide
 * @param {number} divisor - Number to divide by
 * @returns {number} - Remainder
 * @example mod(19, 12) → 7, mod(-19, 12) → -7
 */
function mod(dividend, divisor) {
  return toNumber(dividend) % toNumber(divisor);
}

/**
 * Returns the square root of a number
 * @param {number} num - Number to get square root of
 * @returns {number} - Square root
 * @example sqrt(16) → 4, sqrt(2) → 1.414...
 */
function sqrt(num) {
  return Math.sqrt(toNumber(num));
}

// =============================================================================
// Array Functions
// =============================================================================

/**
 * Returns the length of an array
 * @param {any[]} arr - Array to count
 * @returns {number} - Array length, or 0 if not an array
 * @example count([1, 2, 3]) → 3, count("not array") → 0
 */
function count(arr) {
  return Array.isArray(arr) ? arr.length : 0;
}

/**
 * Filters an array based on a predicate function
 * @param {any[]} arr - Array to filter
 * @param {function} predicate - Function that returns true/false for each element
 * @returns {any[]} - Filtered array, or empty array if invalid inputs
 * @example filter([1, 2, 3, 4], x => x > 2) → [3, 4]
 */
function filter(arr, predicate) {
  if (!Array.isArray(arr) || typeof predicate !== 'function') return [];
  try {
    return arr.filter(predicate);
  } catch {
    return [];
  }
}

/**
 * Maps an array to a new array using a transform function
 * @param {any[]} arr - Array to transform
 * @param {function} transform - Function to apply to each element
 * @returns {any[]} - Transformed array, or empty array if invalid inputs
 * @example map([1, 2, 3], x => x * 2) → [2, 4, 6]
 */
function map(arr, transform) {
  if (!Array.isArray(arr) || typeof transform !== 'function') return [];
  try {
    return arr.map(transform);
  } catch {
    return [];
  }
}

/**
 * Tests whether at least one element passes the predicate function
 * @param {any[]} arr - Array to test
 * @param {function} predicate - Function that returns true/false for each element
 * @returns {boolean} - True if any element passes, false otherwise
 * @example some([1, 2, 3], x => x > 2) → true
 */
function some(arr, predicate) {
  if (!Array.isArray(arr) || typeof predicate !== 'function') return false;
  try {
    return arr.some(predicate);
  } catch {
    return false;
  }
}

/**
 * Tests whether all elements pass the predicate function
 * @param {any[]} arr - Array to test
 * @param {function} predicate - Function that returns true/false for each element
 * @returns {boolean} - True if all elements pass, false otherwise
 * @example every([2, 4, 6], x => x % 2 === 0) → true
 */
function every(arr, predicate) {
  if (!Array.isArray(arr) || typeof predicate !== 'function') return true;
  try {
    return arr.every(predicate);
  } catch {
    return false;
  }
}

/**
 * Reduces an array to a single value using an accumulator function
 * @param {any[]} arr - Array to reduce
 * @param {function} reducer - Function to apply to each element (accumulator, current, index, array)
 * @param {any} initialValue - Initial value for the accumulator
 * @returns {any} - Final accumulated value
 * @example reduce([1, 2, 3], (acc, val) => acc + val, 0) → 6
 */
function reduce(arr, reducer, initialValue) {
  if (!Array.isArray(arr) || typeof reducer !== 'function') return initialValue;
  try {
    return arr.reduce(reducer, initialValue);
  } catch {
    return initialValue;
  }
}

/**
 * Counts the items in an array that match the criterion
 * @param {any[]} arr - Array to search
 * @param {string} criterion - String to match against
 * @returns {number} - Count of matching items
 * @example countIf(["joy", "Joyfill", "hello"], "joy") → 2
 */
function countIf(arr, criterion) {
  if (!Array.isArray(arr)) return 0;
  const criterionStr = toString(criterion).toLowerCase();
  return arr.filter(item => toString(item).toLowerCase().includes(criterionStr)).length;
}

/**
 * Flattens an array up to the specified depth
 * @param {any[]} arr - Array to flatten
 * @param {number} [depth=1] - Depth to flatten to
 * @returns {any[]} - Flattened array
 * @example flat([1, [2, 3]]) → [1, 2, 3], flat([0, 1, [2, [3, [4, 5]]]], 2) → [0, 1, 2, 3, [4, 5]]
 */
function flat(arr, depth = 1) {
  if (!Array.isArray(arr)) return [];
  const d = Math.max(0, Math.floor(toNumber(depth)));
  return d > 0 ? arr.flat(d) : arr;
}

/**
 * Maps and flattens the result by one level
 * @param {any[]} arr - Array to process
 * @param {function} transform - Function to apply to each element
 * @returns {any[]} - Mapped and flattened array
 * @example flatMap([1, 2, 3], item => [item, item]) → [1, 1, 2, 2, 3, 3]
 */
function flatMap(arr, transform) {
  if (!Array.isArray(arr) || typeof transform !== 'function') return [];
  try {
    return arr.flatMap(transform);
  } catch {
    return [];
  }
}

/**
 * Returns the first element that satisfies the predicate function
 * @param {any[]} arr - Array to search
 * @param {function} predicate - Function that returns true/false for each element
 * @returns {any} - First matching element, or undefined if not found
 * @example find([10, 20, 30], item => item > 20) → 30
 */
function find(arr, predicate) {
  if (!Array.isArray(arr) || typeof predicate !== 'function') return undefined;
  try {
    return arr.find(predicate);
  } catch {
    return undefined;
  }
}

// =============================================================================
// Logical Functions
// =============================================================================

/**
 * Conditional function (ternary operator as function)
 * Named 'iff' to avoid conflict with JavaScript's 'if' keyword
 * @param {any} condition - Condition to test
 * @param {any} trueValue - Value to return if condition is truthy
 * @param {any} falseValue - Value to return if condition is falsy
 * @returns {any} - Either trueValue or falseValue
 * @example iff(5 > 3, "yes", "no") → "yes"
 */
function iff(condition, trueValue, falseValue) {
  return condition ? trueValue : falseValue;
}

/**
 * Logical NOT function
 * @param {any} value - Value to negate
 * @returns {boolean} - Logical opposite of the input
 * @example not(true) → false, not(false) → true, not(0) → true
 */
function not(value) {
  return !value;
}

/**
 * Logical AND function - returns true only if all operands are truthy
 * @param {...any} args - Values to test
 * @returns {boolean} - True if all operands are truthy
 * @example and(true, 1, "hello") → true, and(true, false, true) → false
 */
function and(...args) {
  return args.every(arg => !!arg);
}

/**
 * Logical OR function - returns true if any operand is truthy
 * @param {...any} args - Values to test
 * @returns {boolean} - True if any operand is truthy
 * @example or(false, 0, "hello") → true, or(false, 0, null) → false
 */
function or(...args) {
  return args.some(arg => !!arg);
}

/**
 * Checks if a value is empty (null, undefined, empty string, empty array)
 * @param {any} value - Value to check
 * @returns {boolean} - True if value is considered empty
 * @example empty("") → true, empty([]) → true, empty(null) → true, empty("hello") → false
 */
function empty(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

/**
 * Performs strict equality comparison between two values
 * No type coercion is performed - both type and value must be exactly the same
 * Arrays and objects always return false (even if content is identical)
 * @param {any} a - First value to compare
 * @param {any} b - Second value to compare
 * @returns {boolean} - True if values are strictly equal
 * @example strictEqual(1, 1) → true, strictEqual("1", 1) → false, strictEqual([], []) → false
 */
function strictEqual(a, b) {
  // Handle null and undefined
  if (a === null && b === null) return true;
  if (a === undefined && b === undefined) return true;
  if (a === null || a === undefined || b === null || b === undefined) return false;
  
  // Different types are never equal
  if (typeof a !== typeof b) return false;
  
  // Arrays always return false (even if identical content)
  if (Array.isArray(a) || Array.isArray(b)) return false;
  
  // Objects always return false (even if identical content)
  if (typeof a === 'object' || typeof b === 'object') return false;
  
  // For primitive types, use strict equality
  return a === b;
}

/**
 * Performs strict greater than comparison between two values
 * Only works with numbers - all other type combinations return false
 * No type coercion is performed - both operands must be numbers
 * @param {any} a - First value to compare (left operand)
 * @param {any} b - Second value to compare (right operand)
 * @returns {boolean} - True if a > b (numbers only), false otherwise
 * @example strictGreaterThan(12, 10) → true, strictGreaterThan("10", 5) → false, strictGreaterThan(true, false) → false
 */
function strictGreaterThan(a, b) {
  // Both operands must be numbers for the comparison to work
  if (typeof a !== 'number' || typeof b !== 'number') return false;
  
  // Handle NaN - any comparison with NaN should return false
  if (Number.isNaN(a) || Number.isNaN(b)) return false;
  
  // For valid numbers, use standard greater than comparison
  return a > b;
}

/**
 * Performs strict greater than or equal comparison between two values
 * Only works with numbers - all other type combinations return false
 * No type coercion is performed - both operands must be numbers
 * @param {any} a - First value to compare (left operand)
 * @param {any} b - Second value to compare (right operand)
 * @returns {boolean} - True if a >= b (numbers only), false otherwise
 * @example strictGreaterThanOrEqual(12, 10) → true, strictGreaterThanOrEqual(10, 10) → true, strictGreaterThanOrEqual("10", 5) → false, strictGreaterThanOrEqual(true, false) → false
 */
function strictGreaterThanOrEqual(a, b) {
  // Both operands must be numbers for the comparison to work
  if (typeof a !== 'number' || typeof b !== 'number') return false;
  
  // Handle NaN - any comparison with NaN should return false
  if (Number.isNaN(a) || Number.isNaN(b)) return false;
  
  // For valid numbers, use standard greater than or equal comparison
  return a >= b;
}

/**
 * Performs strict less than comparison between two values
 * Only works with numbers - all other type combinations return false
 * No type coercion is performed - both operands must be numbers
 * @param {any} a - First value to compare (left operand)
 * @param {any} b - Second value to compare (right operand)
 * @returns {boolean} - True if a < b (numbers only), false otherwise
 * @example strictLessThan(10, 12) → true, strictLessThan(5, "10") → false, strictLessThan(false, true) → false
 */
function strictLessThan(a, b) {
  // Both operands must be numbers for the comparison to work
  if (typeof a !== 'number' || typeof b !== 'number') return false;
  
  // Handle NaN - any comparison with NaN should return false
  if (Number.isNaN(a) || Number.isNaN(b)) return false;
  
  // For valid numbers, use standard less than comparison
  return a < b;
}

/**
 * Performs strict less than or equal comparison between two values
 * Only works with numbers - all other type combinations return false
 * No type coercion is performed - both operands must be numbers
 * @param {any} a - First value to compare (left operand)
 * @param {any} b - Second value to compare (right operand)
 * @returns {boolean} - True if a <= b (numbers only), false otherwise
 * @example strictLessThanOrEqual(10, 12) → true, strictLessThanOrEqual(10, 10) → true, strictLessThanOrEqual(5, "10") → false, strictLessThanOrEqual(false, true) → false
 */
function strictLessThanOrEqual(a, b) {
  // Both operands must be numbers for the comparison to work
  if (typeof a !== 'number' || typeof b !== 'number') return false;
  
  // Handle NaN - any comparison with NaN should return false
  if (Number.isNaN(a) || Number.isNaN(b)) return false;
  
  // For valid numbers, use standard less than or equal comparison
  return a <= b;
}

// =============================================================================
// String Functions
// =============================================================================

/**
 * Concatenates multiple values into a string
 * @param {...any} args - Values to concatenate
 * @returns {string} - Concatenated string
 * @example concat("Hello", " ", "World") → "Hello World"
 */
function concat(...args) {
  return args.map(arg => {
    if (arg === null || arg === undefined) return '';
    return String(arg);
  }).join('');
}

/**
 * Tests whether the first string contains the second string (case-insensitive)
 * @param {string} str - String to search in
 * @param {string} searchStr - String to search for
 * @returns {boolean} - True if str contains searchStr
 * @example contains("Joyfill Rocks", "rock") → true, contains("Joyfill Rocks", "test") → false
 */
function contains(str, searchStr) {
  const haystack = toString(str).toLowerCase();
  const needle = toString(searchStr).toLowerCase();
  return haystack.includes(needle);
}

/**
 * Converts string to uppercase
 * @param {string} str - String to convert
 * @returns {string} - Uppercase string
 * @example upper("joy") → "JOY"
 */
function upper(str) {
  return toString(str).toUpperCase();
}

/**
 * Converts string to lowercase
 * @param {string} str - String to convert
 * @returns {string} - Lowercase string
 * @example lower("JOY") → "joy"
 */
function lower(str) {
  return toString(str).toLowerCase();
}

/**
 * Returns the length of a string or array
 * @param {string|any[]} value - String or array to measure
 * @returns {number} - Length of the string or array
 * @example length("Joyfill") → 7, length(['opt1', 'opt2']) → 2
 */
function length(value) {
  if (typeof value === 'string') return value.length;
  if (Array.isArray(value)) return value.length;
  return 0;
}

/**
 * Joins array elements into a string with optional separator
 * @param {any[]} arr - Array to join
 * @param {string} separator - Separator string (default: "")
 * @returns {string} - Joined string
 * @example join(["a", "b", "c"], "-") → "a-b-c", join(["a", "b", "c"]) → "abc"
 */
function join(arr, separator = '') {
  if (!Array.isArray(arr)) return toString(arr);
  return arr.map(item => toString(item)).join(separator);
}

// =============================================================================
// Date Functions
// =============================================================================

/**
 * Returns the current date and time as Unix timestamp
 * @returns {number} - Current timestamp in milliseconds
 * @example now() → 1743192431422
 */
function now() {
  return Date.now();
}

/**
 * Returns the year of a date
 * @param {number} timestamp - Unix timestamp in milliseconds
 * @returns {number|null} - Year, or null if timestamp is null/undefined
 * @example year(1743192431422) → 2025, year(null) → null
 */
function year(timestamp) {
  if (timestamp === null || timestamp === undefined) return null;
  const date = new Date(toNumber(timestamp));
  return date.getUTCFullYear();
}

/**
 * Returns the month of a date (1-12)
 * @param {number} timestamp - Unix timestamp in milliseconds
 * @returns {number|null} - Month (1 = January, 12 = December), or null if timestamp is null/undefined
 * @example month(1743192431422) → 6, month(null) → null
 */
function month(timestamp) {
  if (timestamp === null || timestamp === undefined) return null;
  const date = new Date(toNumber(timestamp));
  return date.getUTCMonth() + 1; // JavaScript months are 0-based
}

/**
 * Returns the day of the month from a date (1-31)
 * @param {number} timestamp - Unix timestamp in milliseconds
 * @returns {number|null} - Day of month, or null if timestamp is null/undefined
 * @example day(1743192431422) → 25, day(null) → null
 */
function day(timestamp) {
  if (timestamp === null || timestamp === undefined) return null;
  const date = new Date(toNumber(timestamp));
  return date.getUTCDate();
}

/**
 * Creates a date from year, month, and day
 * @param {number} year - Year
 * @param {number} month - Month (1-12)
 * @param {number} day - Day (1-31)
 * @returns {number} - Unix timestamp in milliseconds
 * @example date(2011, 10, 21) → Unix timestamp
 */
function date(year, month, day) {
  const y = toNumber(year);
  const m = toNumber(month) - 1; // JavaScript months are 0-based
  const d = toNumber(day);
  return new Date(Date.UTC(y, m, d)).getTime();
}

/**
 * Adds time to a date
 * @param {number} timestamp - Unix timestamp in milliseconds
 * @param {number} amount - Amount to add
 * @param {string} unit - Unit of time ("years", "months", "weeks", "days", "hours", "minutes", "seconds", "milliseconds")
 * @returns {number|null} - New timestamp, or null if timestamp is null/undefined
 * @example dateAdd(timestamp, 3, "days") → timestamp + 3 days, dateAdd(null, 3, "days") → null
 */
function dateAdd(timestamp, amount, unit) {
  if (timestamp === null || timestamp === undefined) return null;
  const date = new Date(toNumber(timestamp));
  const amt = toNumber(amount);
  const unitStr = toString(unit).toLowerCase();
  
  switch (unitStr) {
    case 'years':
      date.setUTCFullYear(date.getUTCFullYear() + amt);
      break;
    case 'months':
      date.setUTCMonth(date.getUTCMonth() + amt);
      break;
    case 'weeks':
      date.setUTCDate(date.getUTCDate() + (amt * 7));
      break;
    case 'days':
      date.setUTCDate(date.getUTCDate() + amt);
      break;
    case 'hours':
      date.setUTCHours(date.getUTCHours() + amt);
      break;
    case 'minutes':
      date.setUTCMinutes(date.getUTCMinutes() + amt);
      break;
    case 'seconds':
      date.setUTCSeconds(date.getUTCSeconds() + amt);
      break;
    case 'milliseconds':
      date.setUTCMilliseconds(date.getUTCMilliseconds() + amt);
      break;
    default:
      // Unknown unit, return original timestamp
      return toNumber(timestamp);
  }
  
  return date.getTime();
}

/**
 * Subtracts time from a date
 * @param {number} timestamp - Unix timestamp in milliseconds
 * @param {number} amount - Amount to subtract
 * @param {string} unit - Unit of time ("years", "months", "weeks", "days", "hours", "minutes", "seconds", "milliseconds")
 * @returns {number|null} - New timestamp, or null if timestamp is null/undefined
 * @example dateSubtract(timestamp, 3, "days") → timestamp - 3 days, dateSubtract(null, 3, "days") → null
 */
function dateSubtract(timestamp, amount, unit) {
  if (timestamp === null || timestamp === undefined) return null;
  return dateAdd(timestamp, -toNumber(amount), unit);
}

// =============================================================================
// Export built-ins and metadata
// =============================================================================

export const builtins = {
  // Math functions
  sum,
  average,
  min,
  max,
  round,
  abs,
  pow,
  ceil,
  floor,
  mod,
  sqrt,
  
  // Array functions
  count,
  filter,
  map,
  some,
  every,
  reduce,
  countIf,
  flat,
  flatMap,
  find,
  
  // Logical functions
  iff,
  not,
  and,
  or,
  empty,
  strictEqual,
  strictGreaterThan,
  strictGreaterThanOrEqual,
  strictLessThan,
  strictLessThanOrEqual,
  
  // String functions
  concat,
  contains,
  upper,
  lower,
  length,
  join,
  toNumber,
  toString,
  
  // Date functions
  now,
  year,
  month,
  day,
  date,
  dateAdd,
  dateSubtract,
};

/**
 * Set of all built-in function names
 * Used by the dependency parser to avoid treating built-in names as field references
 */
export const BUILTIN_NAMES = new Set(Object.keys(builtins));